import {
  AddFileParams,
  AddFolderParams,
  Conversation,
  File,
  FileFolderPath,
  Folder,
  Resource,
  User,
} from '@nextforce/api'
import { Button, Icon, makeStyles } from '@rneui/themed'
import { t } from 'app/i18l'
import { isRepository } from 'chat/conversations/utils'
import {
  FileFolderSortOption,
  FileFolderViewMode,
} from 'file_folder_repository/types'
import { AddFileFolderFAB } from 'file_folder_repository/views/add_file_folder_fab'
import {
  AddFileFolderModal,
  AddFileFolderModalType,
} from 'file_folder_repository/views/add_file_folder_modal'
import { Breadcrumbs } from 'file_folder_repository/views/breadcrumb'
import {
  FiltersRow,
  getFilterItems,
} from 'file_folder_repository/views/filters_row'
import { FileFolderSearchBarButton } from 'file_folder_repository/views/search_bar_button'
import { useCallback, useEffect, useState } from 'react'
import { ActivityIndicator, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { errorAlert } from 'shared/alerts'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { FileFolderGridView } from './file_folder_grid'
import { FileFolderListView } from './file_folder_list'
import { FileFolderTimelineView } from './file_folder_timeline'

interface Props {
  conversation: Conversation | undefined
  user: User | undefined
  chatGroupFileFolderList: Resource[] | undefined
  breadcrumbs: FileFolderPath[]
  loading: boolean
  onPressBreadcrumb(index: number): void
  onPressFolder(folder: Folder): void
  onPressFile(file: File): void
  onLongPressResource(resource: Resource): void
  onPerformSelectedAction(): Promise<void>
  onCancelSelectedAction(): void
  selectedFilterIndex: number
  setSelectedFilterIndex(index: number): void
  onPressSearchBarButton(): void
  sortOption: FileFolderSortOption
  selectedAction: string | undefined
  viewMode: FileFolderViewMode
  onUploadFileFolder: (
    fields: AddFileParams & AddFolderParams,
    type: AddFileFolderModalType
  ) => Promise<void>
}

export function ChatGroupFoldersView({
  breadcrumbs,
  chatGroupFileFolderList,
  loading,
  selectedFilterIndex,
  conversation,
  sortOption,
  selectedAction,
  viewMode,
  user,
  onPressSearchBarButton,
  setSelectedFilterIndex,
  onPerformSelectedAction,
  onPressBreadcrumb,
  onPressFolder,
  onPressFile,
  onLongPressResource,
  onCancelSelectedAction,
  onUploadFileFolder,
}: Props) {
  const styles = useStyles()

  const [inPasteMode, setInPasteMode] = useState(false)
  const [isAddFileFolderModalVisible, setAddFileFolderModalVisible] =
    useState(false)
  const [addFileFolderType, setAddFileFolderType] =
    useState<AddFileFolderModalType>('folder')
  const [isUploadingFileFolder, setIsUploadingFileFolder] = useState(false)

  const canUploadFileFolders =
    conversation?.postsAllowed === 'messageOrFile' && isRepository(conversation)

  // The bottom tab bar is only shown when there is more than one tab (i.e. the
  // acknowledgements tab is present). When it is visible it already reserves the
  // bottom safe-area inset, so the screen and FAB must not add it again.
  const showBottomTabBar =
    isRepository(conversation) && !!conversation?.showAcknowledgements

  const filterItems = getFilterItems(isRepository(conversation))

  const openAddFileFolderModal = useCallback((type: AddFileFolderModalType) => {
    setAddFileFolderType(type)
    setAddFileFolderModalVisible(true)
  }, [])

  const onSubmitAddFileFolder = useCallback(
    async (fields: AddFileParams & AddFolderParams) => {
      setIsUploadingFileFolder(true)
      try {
        await onUploadFileFolder(fields, addFileFolderType)
        setAddFileFolderModalVisible(false)
      } catch (e) {
        errorAlert(e)
      }
      setIsUploadingFileFolder(false)
    },
    [onUploadFileFolder, addFileFolderType]
  )

  const onPressResource = useCallback(
    (resource: Resource) => {
      if (resource.type === 'folder') {
        onPressFolder(resource)
      } else {
        onPressFile(resource)
      }
    },
    [onPressFolder, onPressFile]
  )

  useEffect(() => {
    if (selectedAction) {
      setInPasteMode(true)
    } else {
      setInPasteMode(false)
    }
  }, [selectedAction])

  return (
    <SafeAreaView
      edges={showBottomTabBar ? ['left', 'right'] : ['left', 'right', 'bottom']}
      style={styles.container}
    >
      <View style={styles.searchBarContainer}>
        {viewMode !== 'timeline' ? (
          <FiltersRow
            items={filterItems}
            selectedIndex={selectedFilterIndex}
            onSelect={setSelectedFilterIndex}
          />
        ) : null}
        <FileFolderSearchBarButton onPress={onPressSearchBarButton} />
      </View>
      {breadcrumbs.length < 2 ? null : (
        <View style={styles.breadcrumbsContainer}>
          <Breadcrumbs
            breadcrumbs={breadcrumbs}
            onPressBreadcrumb={onPressBreadcrumb}
          />
        </View>
      )}
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator
            size={'large'}
            color={styles.loadingIndicator.color}
          />
        </View>
      ) : viewMode === 'grid' ? (
        <FileFolderGridView
          chatGroupFileFolderList={chatGroupFileFolderList}
          onPress={onPressResource}
          onLongPress={onLongPressResource}
        />
      ) : viewMode === 'list' ? (
        <FileFolderListView
          chatGroupFileFolderList={chatGroupFileFolderList}
          onLongPressResource={onLongPressResource}
          onPress={onPressResource}
          sortOption={sortOption}
        />
      ) : (
        <FileFolderTimelineView
          chatGroupFileFolderList={chatGroupFileFolderList}
          onLongPressResource={onLongPressResource}
          onPress={onPressResource}
          user={user}
        />
      )}
      {canUploadFileFolders ? (
        <AddFileFolderFAB
          marginBottom={inPasteMode ? 55 : undefined}
          onPressAddFileFolderModal={openAddFileFolderModal}
          useBottomInset={!showBottomTabBar}
        />
      ) : null}
      {inPasteMode ? (
        <PasteButton
          onPress={onPerformSelectedAction}
          onPressCancel={onCancelSelectedAction}
          title={selectedAction === 'copy' ? t('copyHere') : t('moveHere')}
        />
      ) : null}
      <AddFileFolderModal
        type={addFileFolderType}
        isVisible={isAddFileFolderModalVisible}
        onClose={() => setAddFileFolderModalVisible(false)}
        onSubmit={onSubmitAddFileFolder}
        loading={isUploadingFileFolder}
      />
    </SafeAreaView>
  )
}

const PasteButton = ({
  onPress,
  onPressCancel,
  title,
}: {
  onPress(): Promise<void>
  onPressCancel(): void
  title: string
}) => {
  const styles = useStyles()
  const [loading, setLoading] = useState(false)
  const onPressPaste = useCallback(async () => {
    setLoading(true)
    try {
      await onPress()
    } catch (e) {
      errorAlert(e)
    }
    setLoading(false)
  }, [onPress])

  return (
    <View style={styles.pasteComponentContainer}>
      <Button
        title={title}
        onPress={onPressPaste}
        loading={loading}
        containerStyle={styles.pasteButtonContainer}
      />
      <PressableOpacity onPress={onPressCancel} style={styles.cancelButton}>
        <Icon name="cancel" color={styles.cancelButton.color} size={40} />
      </PressableOpacity>
    </View>
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
  },
  searchBarContainer: { marginHorizontal: 10, marginTop: 10 },
  breadcrumbsContainer: { padding: 10 },
  fileFolderListContainer: {
    paddingBottom: 100,
    flexGrow: 1,
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.white : theme.colors.grey6,
  },
  pasteComponentContainer: {
    padding: 10,
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  pasteButtonContainer: {
    borderRadius: 20,
    backgroundColor: theme.colors.primary,
    flex: 1,
    marginRight: 10,
    justifyContent: 'center',
  },
  loadingIndicator: {
    color: theme.colors.primary,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.white : theme.colors.grey6,
  },
  emptyComponent: { flex: 1, justifyContent: 'center' },
  emptyComponentText: { textAlign: 'center' },
  cancelButton: {
    borderRadius: 50,
    justifyContent: 'center',
    alignItems: 'center',
    height: 40,
    width: 40,
    color: theme.colors.black,
  },
}))
